import { AddRounded } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { fetchJournalEntries } from "@/api/accounting";
import JournalEntryDetail from "@/components/accounting/JournalEntryDetail";
import NewJournalEntry from "@/components/accounting/NewJournalEntry";
import SourcePicker from "@/components/accounting/SourcePicker";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import { JOURNAL_ENTRIES_PAGE_SIZE, sourceTypeLabel } from "@/constants/accounting";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { useJournalEntries } from "@/hooks/useAccounting";
import { usePermissions } from "@/hooks/usePermissions";
import type { JournalEntry } from "@/interfaces/accounting";
import { formatDate } from "@/utils/date-format";

const money = (v: string | null) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 0 draft · 1 posted · 2 needs review — see JournalEntryStatus on the API. */
interface StatusChip {
  label: string;
  color: "default" | "success" | "warning";
}
const DRAFT: StatusChip = { label: "Draft", color: "default" };
const STATUS: Record<number, StatusChip> = {
  0: DRAFT,
  1: { label: "Posted", color: "success" },
  2: { label: "Needs review", color: "warning" },
};
/** An unknown status shows as Draft rather than blowing up the row. */
const statusChip = (status: number): StatusChip => STATUS[status] ?? DRAFT;

/** 0 normal · 1 adjustment · 2 reversal. Only the corrections are worth calling out. */
const ENTRY_TYPE: Record<number, string> = { 1: "Adjustment", 2: "Reversal" };

/**
 * Accounting › Journal Entries — the subledger blotter. GFG-only (view/FINANCE, scope all).
 *
 * The date range filters on the ACCOUNTING date (the book's date), not when the entry was recorded,
 * so a back-dated correction appears in the period it corrects. `asOnDate` is shown alongside and
 * differs from it exactly when that has happened.
 *
 * The pickers live here rather than in the Blotter's filter bar, which only does multi-select and
 * boolean fields. Changing a date resets to page 1 via the Blotter's own key.
 */
export default function JournalEntriesPanel() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [sourceType, setSourceType] = useState<number | "">("");
  const [sourceId, setSourceId] = useState<number | null>(null);
  const { can } = usePermissions();
  // Writing an entry by hand is a money path, gated the same as a reversal or a payout record.
  const canCreate = can("cancel", "FINANCE");

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<JournalEntry> => {
    const { data, isLoading } = useJournalEntries({
      sourceType: sourceType === "" ? undefined : sourceType,
      sourceId: sourceId ?? undefined,
      from: from || undefined,
      to: to || undefined,
      search: q.search || undefined,
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  if (creating) return <NewJournalEntry onDone={() => setCreating(false)} />;
  if (selectedId != null) {
    return <JournalEntryDetail journalEntryId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const config: BlotterConfig<JournalEntry> = {
    title: "Journal Entries",
    searchPlaceholder: "Search memo, journal no, reason, or organization",
    itemNoun: "entry",
    rowKey: (e) => e.id,
    defaultSortKey: "accountingDate",
    defaultSortDir: "desc",
    pageSize: JOURNAL_ENTRIES_PAGE_SIZE,
    filterFields: [],
    useData,
    exportFilename: "journal-entries.csv",
    // Dumps the visible columns. NOT the QuickBooks import file — that one is built in Periods &
    // Posting, where it's raised as a batch rather than downloaded from a blotter.
    exportFetch: async (q) => {
      const res = await fetchJournalEntries({
        sourceType: sourceType === "" ? undefined : sourceType,
        sourceId: sourceId ?? undefined,
        from: from || undefined,
        to: to || undefined,
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    headerAction: canCreate ? (
      <IconButton
        onClick={() => setCreating(true)}
        title="New journal entry"
        aria-label="New journal entry"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <AddRounded />
      </IconButton>
    ) : undefined,
    columns: [
      {
        // First column, and the way into the entry — the same affordance the invoice blotter uses
        // for its invoice number.
        key: "journalNo",
        label: "Journal no",
        sortable: true,
        sortValue: (e) => e.journalNo,
        render: (e) => (
          <Button
            variant="text"
            onClick={() => setSelectedId(e.id)}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: 0,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: "var(--color-rodeo-black)",
            }}
          >
            {e.journalNo}
          </Button>
        ),
        exportValue: (e) => e.journalNo,
      },
      {
        key: "accountingDate",
        label: "Accounting date",
        sortable: true,
        sortValue: (e) => e.accountingDate,
        render: (e) => <Box sx={{ fontWeight: 500 }}>{formatDate(e.accountingDate)}</Box>,
        exportValue: (e) => e.accountingDate,
      },
      {
        // The knowledge date. Equal to the accounting date unless the entry was back-dated.
        key: "asOnDate",
        label: "Posted date",
        sortable: true,
        sortValue: (e) => e.asOnDate,
        render: (e) => formatDate(e.asOnDate),
      },
      {
        key: "reason",
        label: "Event",
        sortValue: (e) => e.reasonLabel,
        render: (e) => (
          <Box>
            <Box sx={{ fontWeight: 500 }}>{e.reasonLabel}</Box>
            <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>
              {e.reasonCode}
              {ENTRY_TYPE[e.entryType] ? ` · ${ENTRY_TYPE[e.entryType]}` : ""}
            </Typography>
          </Box>
        ),
        exportValue: (e) => e.reasonCode,
      },
      {
        key: "sourceType",
        label: "Source",
        sortValue: (e) => sourceTypeLabel(e.sourceType),
        render: (e) => (
          <Box>
            <Box>{sourceTypeLabel(e.sourceType)}</Box>
            {e.sourceId != null && (
              <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>
                #{e.sourceId}
              </Typography>
            )}
          </Box>
        ),
        exportValue: (e) =>
          `${sourceTypeLabel(e.sourceType)}${e.sourceId != null ? ` #${e.sourceId}` : ""}`,
      },
      {
        key: "organization",
        label: "Organization",
        sortValue: (e) => e.organizationName ?? "",
        render: (e) => e.organizationName ?? "—",
      },
      {
        key: "group",
        label: "Group",
        sortValue: (e) => e.groupName ?? "",
        render: (e) => e.groupName ?? "—",
      },
      {
        key: "totalDebit",
        label: "Dr",
        sortable: true,
        sortValue: (e) => Number(e.totalDebit),
        render: (e) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums", textAlign: "right", fontWeight: 500 }}>
            {money(e.totalDebit)}
          </Box>
        ),
        exportValue: (e) => e.totalDebit,
      },
      {
        key: "totalCredit",
        label: "Cr",
        sortable: true,
        sortValue: (e) => Number(e.totalCredit),
        render: (e) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums", textAlign: "right", fontWeight: 500 }}>
            {money(e.totalCredit)}
          </Box>
        ),
        exportValue: (e) => e.totalCredit,
      },
      {
        key: "actor",
        label: "Actioned by",
        sortValue: (e) => e.actorName ?? "",
        render: (e) =>
          e.actorName ?? (
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>System</Typography>
          ),
        exportValue: (e) => e.actorName ?? "System",
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (e) => e.status,
        render: (e) => {
          const s = statusChip(e.status);
          return <Chip size="small" variant="outlined" label={s.label} color={s.color} />;
        },
        exportValue: (e) => statusChip(e.status).label,
      },
    ],
  };

  // Remount on a date change so pagination resets — page 3 of the old range is meaningless.
  return (
    <Box>
      {/* Above the search bar rather than in the header slot: the range frames what the search is
          searching, so it reads first. */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <TextField
          type="date"
          lang={DATE_INPUT_LANG}
          size="small"
          label="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 175 }}
        />
        <TextField
          type="date"
          lang={DATE_INPUT_LANG}
          size="small"
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 175 }}
        />
        <SourcePicker
          sourceType={sourceType}
          sourceId={sourceId}
          onChange={(t, id) => {
            setSourceType(t);
            setSourceId(id);
          }}
          entityLabel="Any"
        />
        {(from || to || sourceType !== "") && (
          <Button
            size="small"
            onClick={() => {
              setFrom("");
              setTo("");
              setSourceType("");
              setSourceId(null);
            }}
            sx={{ textTransform: "none", color: "var(--color-graphite)" }}
          >
            Clear
          </Button>
        )}
      </Box>
      <Blotter key={`${from}|${to}|${sourceType}|${sourceId}`} config={config} />
    </Box>
  );
}
